import { QueryConditionType } from './QueryTypes';
import { BitMask64Utils } from '../Utils/BigIntCompatibility';
import { createLogger } from '../../Utils/Logger';
const logger = createLogger('ReactiveQuery');
/**
 * 响应式查询变化类型
 */
export var ReactiveQueryChangeType;
(function (ReactiveQueryChangeType) {
    /** 实体添加到查询结果 */
    ReactiveQueryChangeType["ADDED"] = "added";
    /** 实体从查询结果移除 */
    ReactiveQueryChangeType["REMOVED"] = "removed";
    /** 查询结果批量更新 */
    ReactiveQueryChangeType["BATCH_UPDATE"] = "batch_update";
})(ReactiveQueryChangeType || (ReactiveQueryChangeType = {}));
/**
 * 响应式查询类
 *
 * 提供基于事件驱动的实体查询机制,只在实体/组件真正变化时触发通知。
 *
 * 核心特性:
 * - Event-driven: 基于事件的增量更新
 * - 精确通知: 只通知真正匹配的变化
 * - 性能优化: 避免每帧重复查询
 *
 * @example
 * ```typescript
 * // 创建响应式查询
 * const query = new ReactiveQuery(querySystem, {
 *     type: QueryConditionType.ALL,
 *     componentTypes: [Position, Velocity],
 *     mask: createMask([Position, Velocity])
 * });
 *
 * // 订阅变化
 * query.subscribe((change) => {
 *     if (change.type === ReactiveQueryChangeType.ADDED) {
 *         console.log('新实体:', change.entity);
 *     }
 * });
 *
 * // 获取当前结果
 * const entities = query.getEntities();
 * ```
 */
export class ReactiveQuery {
    constructor(condition, config = {}) {
        /** 当前查询结果 */
        this._entities = [];
        /** 实体ID集合,用于快速查找 */
        this._entityIdSet = new Set();
        /**
         * 实体数组快照 - 用于安全迭代
         * Entity array snapshot - for safe iteration
         * 只在实体列表变化时才创建新快照，静态场景下所有系统共享同一快照
         * Only create new snapshot when entity list changes, static scenes share the same snapshot
         */
        this._snapshot = null;
        /** 监听器列表 */
        this._listeners = [];
        /** 是否已激活 */
        this._active = true;
        this._condition = condition;
        this._config = {
            enableBatchMode: config.enableBatchMode ?? true,
            batchDelay: config.batchDelay ?? 16, // 默认一帧
            debug: config.debug ?? false
        };
        this._id = this.generateQueryId();
        this._batchChanges = {
            added: [],
            removed: [],
            timer: null
        };
        if (this._config.debug) {
            logger.debug(`创建ReactiveQuery: ${this._id}`);
        }
    }
    /**
     * 生成查询ID
     */
    generateQueryId() {
        const typeStr = this._condition.type;
        const componentsStr = this._condition.componentTypes
            .map((t) => t.name)
            .sort()
            .join(',');
        return `${typeStr}:${componentsStr}`;
    }
    /**
     * 订阅查询变化
     *
     * @param listener 监听器函数
     * @returns 取消订阅的函数
     */
    subscribe(listener) {
        if (!this._active) {
            throw new Error(`Cannot subscribe to disposed ReactiveQuery ${this._id}`);
        }
        if (typeof listener !== 'function') {
            throw new TypeError('Listener must be a function');
        }
        this._listeners.push(listener);
        if (this._config.debug) {
            logger.debug(`订阅ReactiveQuery: ${this._id}, 监听器数量: ${this._listeners.length}`);
        }
        return () => {
            const index = this._listeners.indexOf(listener);
            if (index !== -1) {
                this._listeners.splice(index, 1);
            }
        };
    }
    /**
     * 取消所有订阅
     */
    unsubscribeAll() {
        this._listeners.length = 0;
    }
    /**
     * 获取当前查询结果（返回安全快照）
     * Get current query results (returns safe snapshot)
     *
     * 返回的快照在实体列表变化前保持不变，可安全用于迭代。
     * 静态场景下所有系统共享同一快照，避免每帧创建数组副本。
     *
     * The returned snapshot remains unchanged until entity list changes, safe for iteration.
     * Static scenes share the same snapshot, avoiding array copy every frame.
     */
    getEntities() {
        // 如果快照有效，直接返回 | Return snapshot if valid
        if (this._snapshot !== null) {
            return this._snapshot;
        }
        // 创建新快照 | Create new snapshot
        this._snapshot = [...this._entities];
        return this._snapshot;
    }
    /**
     * 获取查询结果数量
     */
    get count() {
        return this._entities.length;
    }
    /**
     * 检查实体是否匹配查询条件
     *
     * @param entity 要检查的实体
     * @returns 是否匹配
     */
    matches(entity) {
        const entityMask = entity.componentMask;
        switch (this._condition.type) {
            case QueryConditionType.ALL:
                return BitMask64Utils.hasAll(entityMask, this._condition.mask);
            case QueryConditionType.ANY:
                return BitMask64Utils.hasAny(entityMask, this._condition.mask);
            case QueryConditionType.NONE:
                return BitMask64Utils.hasNone(entityMask, this._condition.mask);
            default:
                return false;
        }
    }
    /**
     * 通知实体添加
     *
     * 当Scene中添加实体时调用
     *
     * @param entity 添加的实体
     */
    notifyEntityAdded(entity) {
        if (!this._active)
            return;
        // 检查实体是否匹配查询条件
        if (!this.matches(entity)) {
            return;
        }
        // 检查是否已存在
        if (this._entityIdSet.has(entity.id)) {
            return;
        }
        // 添加到结果集
        this._entities.push(entity);
        this._entityIdSet.add(entity.id);
        this._snapshot = null; // 使快照失效 | Invalidate snapshot
        // 通知监听器
        if (this._config.enableBatchMode) {
            this.addToBatch('added', entity);
        }
        else {
            this.notifyListeners({
                type: ReactiveQueryChangeType.ADDED,
                entity
            });
        }
        if (this._config.debug) {
            logger.debug(`ReactiveQuery ${this._id}: 实体添加 ${entity.name}(${entity.id})`);
        }
    }
    /**
     * 通知实体移除
     *
     * 当Scene中移除实体时调用
     *
     * @param entity 移除的实体
     */
    notifyEntityRemoved(entity) {
        if (!this._active)
            return;
        // 检查是否在结果集中
        if (!this._entityIdSet.has(entity.id)) {
            return;
        }
        // 从结果集移除
        const index = this._entities.indexOf(entity);
        if (index !== -1) {
            this._entities.splice(index, 1);
        }
        this._entityIdSet.delete(entity.id);
        this._snapshot = null; // 使快照失效 | Invalidate snapshot
        // 通知监听器
        if (this._config.enableBatchMode) {
            this.addToBatch('removed', entity);
        }
        else {
            this.notifyListeners({
                type: ReactiveQueryChangeType.REMOVED,
                entity
            });
        }
        if (this._config.debug) {
            logger.debug(`ReactiveQuery ${this._id}: 实体移除 ${entity.name}(${entity.id})`);
        }
    }
    /**
     * 通知实体组件变化
     *
     * 当实体的组件发生变化时调用
     *
     * @param entity 变化的实体
     */
    notifyEntityChanged(entity) {
        if (!this._active)
            return;
        const wasMatching = this._entityIdSet.has(entity.id);
        const isMatching = this.matches(entity);
        if (wasMatching && !isMatching) {
            // 实体不再匹配,从结果集移除
            this.notifyEntityRemoved(entity);
        }
        else if (!wasMatching && isMatching) {
            // 实体现在匹配,添加到结果集
            this.notifyEntityAdded(entity);
        }
    }
    /**
     * 批量初始化查询结果
     *
     * @param entities 初始实体列表
     */
    initializeWith(entities) {
        // 清空现有结果
        this._entities.length = 0;
        this._entityIdSet.clear();
        this._snapshot = null; // 使快照失效 | Invalidate snapshot
        // 筛选匹配的实体
        for (const entity of entities) {
            if (this.matches(entity)) {
                this._entities.push(entity);
                this._entityIdSet.add(entity.id);
            }
        }
        if (this._config.debug) {
            logger.debug(`ReactiveQuery ${this._id}: 初始化 ${this._entities.length} 个实体`);
        }
    }
    /**
     * 添加到批量变化缓存
     */
    addToBatch(type, entity) {
        if (type === 'added') {
            this._batchChanges.added.push(entity);
        }
        else {
            this._batchChanges.removed.push(entity);
        }
        // 启动批量通知定时器
        if (this._batchChanges.timer === null) {
            this._batchChanges.timer = setTimeout(() => {
                this.flushBatchChanges();
            }, this._config.batchDelay);
        }
    }
    /**
     * 刷新批量变化
     */
    flushBatchChanges() {
        if (this._batchChanges.added.length === 0 && this._batchChanges.removed.length === 0) {
            this._batchChanges.timer = null;
            return;
        }
        const added = [...this._batchChanges.added];
        const removed = [...this._batchChanges.removed];
        // 清空缓存
        this._batchChanges.added.length = 0;
        this._batchChanges.removed.length = 0;
        this._batchChanges.timer = null;
        // 通知监听器
        this.notifyListeners({
            type: ReactiveQueryChangeType.BATCH_UPDATE,
            added,
            removed,
            entities: this._entities
        });
        if (this._config.debug) {
            logger.debug(`ReactiveQuery ${this._id}: 批量更新 +${added.length} -${removed.length}`);
        }
    }
    /**
     * 通知所有监听器
     */
    notifyListeners(change) {
        const listeners = [...this._listeners];
        for (const listener of listeners) {
            try {
                listener(change);
            }
            catch (error) {
                logger.error(`ReactiveQuery ${this._id}: 监听器执行出错`, error);
            }
        }
    }
    /**
     * 暂停响应式查询
     *
     * 暂停后不再响应实体变化,但可以继续获取当前结果
     */
    pause() {
        this._active = false;
        // 清空批量变化缓存
        if (this._batchChanges.timer !== null) {
            clearTimeout(this._batchChanges.timer);
            this._batchChanges.timer = null;
        }
        this._batchChanges.added.length = 0;
        this._batchChanges.removed.length = 0;
    }
    /**
     * 恢复响应式查询
     */
    resume() {
        this._active = true;
    }
    /**
     * 销毁响应式查询
     *
     * 释放所有资源,清空监听器和结果集
     */
    dispose() {
        if (this._batchChanges.timer !== null) {
            clearTimeout(this._batchChanges.timer);
            this._batchChanges.timer = null;
        }
        this._batchChanges.added.length = 0;
        this._batchChanges.removed.length = 0;
        this._active = false;
        this.unsubscribeAll();
        this._entities.length = 0;
        this._entityIdSet.clear();
        this._snapshot = null;
        if (this._config.debug) {
            logger.debug(`ReactiveQuery ${this._id}: 已销毁`);
        }
    }
    /**
     * 获取查询条件
     */
    get condition() {
        return this._condition;
    }
    /**
     * 获取查询ID
     */
    get id() {
        return this._id;
    }
    /**
     * 检查是否激活
     */
    get active() {
        return this._active;
    }
    /**
     * 获取监听器数量
     */
    get listenerCount() {
        return this._listeners.length;
    }
}
//# sourceMappingURL=ReactiveQuery.js.map